import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { useAccountReadContext } from '@app/context/AccountContextContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import {
  CalculationMethod,
  PayoutSchemeAssetClassType,
  TradingAccountType,
} from '@model/MultiTierDto'
import { PayoutSchemeDto } from '@model/PayoutScheme'

export interface PayoutSchemeMetaData {
  hasOnlyExcludedAccountTypes: boolean
}

export enum PayoutSchemeTab {
  FxMetals = 1,
  Crypto = 2,
  Other = 3,
}

export const usePayoutScheme = () => {
  const { account } = useAccountReadContext()
  const apiClient = useApiClient(ClientApiClient)

  const ibId = account?.clientIntroducingBroker?.id

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.ib.payoutScheme(ibId),
    queryFn: () => apiClient.getPayoutScheme(ibId!),
    enabled: !!ibId,
    placeholderData: [],
  })

  const schemeMetaDataByTabId = useMemo(() => {
    const map = new Map<number, PayoutSchemeMetaData>()
    data?.forEach((scheme) => {
      map.set(scheme.assetClass.id, {
        hasOnlyExcludedAccountTypes: scheme.accountTypes.every(
          ({ accountType, calculationMethod, instruments }) =>
            calculationMethod.id === CalculationMethod.PER_MILLION_USD ||
            accountType?.id === TradingAccountType.MAM_Investor ||
            accountType?.id === TradingAccountType.Pro ||
            accountType?.id === TradingAccountType.VIP ||
            instruments !== null
        ),
      })
    })
    return map
  }, [data])

  const hasOnlyExcludedProfileSettings = Array.from(schemeMetaDataByTabId.values()).every(
    (tabItem) => tabItem.hasOnlyExcludedAccountTypes
  )

  const isPayoutSchemeVisible =
    Array.isArray(data) && data.length > 0 && !hasOnlyExcludedProfileSettings

  const filteredData = useMemo((): PayoutSchemeDto[] => {
    return (
      data?.map((scheme) => {
        const isFxMetals = scheme.assetClass.id === PayoutSchemeAssetClassType.FxMetals

        const isClassicSpread = (item: (typeof scheme.accountTypes)[number]) =>
          item.accountType?.id === TradingAccountType.Classic &&
          item.calculationMethod.id === CalculationMethod.PER_SPREAD_SHARE

        const filteredAccountTypes = scheme.accountTypes
          .filter((type) => type.calculationMethod.id !== CalculationMethod.PER_MILLION_USD)
          .sort((a, b) => {
            if (!isFxMetals) return 0

            const priorityA = isClassicSpread(a) ? 0 : 1
            const priorityB = isClassicSpread(b) ? 0 : 1

            return priorityA - priorityB
          })

        return { ...scheme, accountTypes: filteredAccountTypes }
      }) ?? []
    )
  }, [data])

  return { data: filteredData, isLoading, isPayoutSchemeVisible, schemeMetaDataByTabId }
}
